// The Site import workbook (handoff §7g "Excel import"): Instructions,
// Locations, Varieties and Boundaries, generated for one Site so its headings
// are that Site's level names and its dropdown is that Site's live variety
// list. The dropdowns, number limits and the locked Varieties sheet help
// whoever fills it in; they enforce nothing. The server reads every cell
// again on upload and at import (services/structure-import.js), and these
// headings are the ones it looks for.

import { CellValue, Sheet, Validation, workbook } from '../batches/xlsx'
import type { SiteVariety, StructureSite } from './structure'

// The lines a Locations sheet may hold (MAX_LINES on the server), so the
// dropdown and limits cover every line that can be imported at once.
export const TEMPLATE_LINES = 5000
const BOUNDARY_LINES = 20000

const lower = (s: string) => s.toLowerCase()
const pad = (n: number) => String(n).padStart(2, '0')
const stamp = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`

export const locationHeadings = (site: StructureSite) => [site.level1_label, site.level2_label, site.level3_label, 'Location ID', 'Variety', 'Plants']
// Down to the mapping level only: a deeper name would be refused.
export const boundaryHeadings = (site: StructureSite) =>
  [site.level1_label, site.level2_label, site.level3_label].slice(0, site.map_level).concat(['Point order', 'Latitude', 'Longitude'])

export function templateFileName(site: StructureSite): string {
  const safe = site.name.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Site'
  return `${safe} import.xlsx`
}

export function templateSheets(site: StructureSite, varieties: SiteVariety[], now: Date): Sheet[] {
  const labels = [site.level1_label, site.level2_label, site.level3_label]
  const row = lower(labels[2])
  const live = varieties.filter((v) => !v.archived_at)
  const mapLabel = labels[site.map_level - 1]
  const example = live[0]?.name || 'a variety from the Varieties sheet'
  const bHead = boundaryHeadings(site)

  const instructions: CellValue[][] = [
    ['Site import', site.name],
    ['Site', site.name],
    ['Generated', stamp(now)],
    ['Levels', labels.join(' / ')],
    ['Mapping level', `${mapLabel} (level ${site.map_level}): boundaries are drawn around each ${lower(mapLabel)}`],
    [],
    ['What it does', `Adds to this Site only. It never changes a ${row} or a boundary the Site already has.`],
    ['', `Missing ${lower(labels[0])}s and ${lower(labels[1])}s are created with the ${row}s under them.`],
    ['', `A line already on the Site is skipped, so a corrected workbook can be uploaded again without adding anything twice.`],
    ['', 'Control shows a preview before anything is written: the counts, every held-back line with its spreadsheet row number and the reason, and the boundaries on the map.'],
    ['', 'The workbook is checked again when you import. A line with a problem is held back whole; nothing is created for it.'],
    [],
    ['Locations sheet', `One line per ${row}.`],
    ['Required', `${labels.join(', ')}, Variety and Plants.`],
    ['Location ID', 'Optional. Leave it blank and one is generated. An ID already in use anywhere in the workspace is held back, never reused.'],
    ['Variety', `Choose from the dropdown: it lists this Site's live varieties (the Varieties sheet). A variety that is not on the list is held back, never created. Add it under Varieties in Control first.`],
    ['Plants', 'A whole number.'],
    [],
    ['Boundaries sheet', `Optional. One line per corner point of a ${lower(mapLabel)}'s boundary.`],
    ['', `Fill in ${bHead.slice(0, site.map_level).join(', ')}, then Point order, Latitude and Longitude as decimal degrees.`],
    ['', 'The points are joined in Point order and the shape closes itself. One simple shape each: no holes, no separate parts.'],
    ['', `A ${lower(mapLabel)} that already has a boundary keeps it. Reshape it on the Map instead.`],
    [],
    ['Example Locations lines', ''],
    locationHeadings(site),
    [`${labels[0]} A`, `${labels[1]} 1`, `${labels[2]} 1`, '', example, 120],
    [`${labels[0]} A`, `${labels[1]} 1`, `${labels[2]} 2`, '', example, 118],
    [],
    ['Example Boundaries lines', ''],
    bHead,
    ...[
      [1, -37.81, 145.1],
      [2, -37.81, 145.101],
      [3, -37.8105, 145.101],
      [4, -37.8105, 145.1],
    ].map(([order, lat, lng]) => [...[`${labels[0]} A`, `${labels[1]} 1`, `${labels[2]} 1`].slice(0, site.map_level), order, lat, lng]),
  ]

  const n = live.length
  const locRules: Validation[] = [
    { sqref: `F2:F${TEMPLATE_LINES + 1}`, whole: [0, 10000000], error: 'Plants is a whole number.' },
  ]
  if (n) locRules.unshift({ sqref: `E2:E${TEMPLATE_LINES + 1}`, list: `Varieties!$B$2:$B$${n + 1}`, error: "Choose a variety from this Site's list (the Varieties sheet)." })

  const k = site.map_level
  const col = (i: number) => String.fromCharCode(65 + i)
  const bRules: Validation[] = [
    { sqref: `${col(k)}2:${col(k)}${BOUNDARY_LINES + 1}`, whole: [0, 1000000], error: 'Point order is a whole number.' },
    { sqref: `${col(k + 1)}2:${col(k + 1)}${BOUNDARY_LINES + 1}`, decimal: [-90, 90], error: 'Latitude is a decimal number between -90 and 90.' },
    { sqref: `${col(k + 2)}2:${col(k + 2)}${BOUNDARY_LINES + 1}`, decimal: [-180, 180], error: 'Longitude is a decimal number between -180 and 180.' },
  ]

  return [
    { name: 'Instructions', rows: instructions, widths: [24, 110] },
    { name: 'Locations', rows: [locationHeadings(site)], widths: [18, 18, 18, 14, 28, 10], validations: locRules },
    {
      name: 'Varieties',
      // The id is the list entry's own: the name never changes, and an
      // archived name is not offered (it would be held back).
      rows: [['Variety ID', 'Variety'], ...live.map((v) => [v.id, v.name])],
      widths: [40, 30],
      protect: true,
    },
    { name: 'Boundaries', rows: [bHead], widths: [...bHead.slice(0, k).map(() => 18), 12, 14, 14], validations: bRules },
  ]
}

export const templateWorkbook = (site: StructureSite, varieties: SiteVariety[], now: Date) => workbook(templateSheets(site, varieties, now))
